import { addAlert } from '../alerts.js';
import { initializeSelect } from '../manuals/form.js';
import { saveWindowState } from '../windows.js';
import { sendPostRequest, debounce } from '../utils.js';

function formatDateForAPI(dateInput) {
    if (!dateInput || !dateInput.value) return '';

    const value = dateInput.value;

    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
    }

    // DD.MM.YYYY -> YYYY-MM-DD
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
        const [day, month, year] = value.split('.');
        return `${year}-${month}-${day}`;
    }

    // DD/MM/YYYY -> YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        const [day, month, year] = value.split('/');
        return `${year}-${month}-${day}`;
    }

    try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
    } catch (e) {
        console.error('Error parsing date:', e);
    }

    console.warn('Could not parse date:', value);
    return value;
}

const reportPopup = {
    // --- DOM Elements ---
    form: null,
    modalContainer: null,
    componentGroupSelect: null,
    componentTypeSelect: null,
    enovaIdInput: null,
    rangeFromInput: null,
    rangeToInput: null,
    matchingComponentsCountContainer: null,
    matchingComponentsCount: null,
    measurementStationsSelect: null,
    dateFromInput: null,
    dateToInput: null,
    submitBtn: null,
    btnText: null,
    btnLoader: null,
    titleElement: null,

    // --- State ---
    componentGroupChoicesInstance: null,
    componentTypeChoicesInstance: null,
    stationsChoicesInstance: null,
    isSubmitting: false,

    init() {
        this.form = document.getElementById('reportWindow');
        if (!this.form) {
            console.warn('Report form not found');
            return;
        }

        if (this.form._reportPopupInitialized) {
            console.warn('Report popup already initialized');
            return;
        }
        this.form._reportPopupInitialized = true;

        this._cacheDOMElements();
        this._initializeChoices();
        this._bindEvents();

        const savedStateRaw = localStorage.getItem(
            `windowState-${this.form.id}`,
        );
        if (savedStateRaw) {
            try {
                const savedState = JSON.parse(savedStateRaw);
                if (savedState.open) {
                    this.open();
                }
            } catch (error) {
                console.error('Failed to parse saved window state:', error);
            }
        }
    },

    _cacheDOMElements() {
        this.modalContainer = this.form.closest('.window-modal');
        this.componentGroupSelect = this.form.querySelector(
            '#id_component_group',
        );
        this.componentTypeSelect =
            this.form.querySelector('#id_component_type');
        this.enovaIdInput = this.form.querySelector('#id_enova_id');
        this.rangeFromInput = this.form.querySelector('#id_range_from');
        this.rangeToInput = this.form.querySelector('#id_range_to');
        this.matchingComponentsCountContainer = this.form.querySelector(
            '#matchingComponentsCountContainer',
        );
        this.matchingComponentsCount = this.form.querySelector(
            '#matchingComponentsCount',
        );

        this.measurementStationsSelect = this.form.querySelector(
            '#id_measurement_stations',
        );
        this.dateFromInput = this.form.querySelector('#id_date_from_alt');
        this.dateToInput = this.form.querySelector('#id_date_to_alt');
        this.supplyTypesSelect = this.form.querySelector('#id_supply_types');
        this.includeEmptySupplyTypesCheckbox = this.form.querySelector(
            '#id_include_empty_supply_types',
        );
        this.submitBtn = this.form.querySelector('button[type="submit"]');
        this.btnText = this.submitBtn?.querySelector('.btn-text');
        this.btnLoader = this.submitBtn?.querySelector('.btn-loader');
        this.titleElement = this.form.querySelector('.window-title');
    },

    _initializeChoices() {
        if (this.componentGroupSelect) {
            initializeSelect(this.componentGroupSelect, {
                searchEnabled: false,
                itemSelectText: gettext('Wybierz'),
            });
            this.componentGroupChoicesInstance =
                this.componentGroupSelect._choices;
            this.componentGroupChoicesInstance.removeActiveItems();
        }

        if (this.componentTypeSelect) {
            initializeSelect(this.componentTypeSelect, {
                searchEnabled: false,
                itemSelectText: gettext('Wybierz'),
            });
            this.componentTypeChoicesInstance =
                this.componentTypeSelect._choices;
            this.componentTypeChoicesInstance.removeActiveItems();
        }

        if (this.measurementStationsSelect) {
            initializeSelect(this.measurementStationsSelect, {
                searchPlaceholderValue: gettext('Wyszukaj stanowisko...'),
                itemSelectText: gettext('Wybierz'),
                removeItemButton: true,
            });
            this.stationsChoicesInstance =
                this.measurementStationsSelect._choices;
            this.stationsChoicesInstance.removeActiveItems();
        }

        if (this.supplyTypesSelect) {
            initializeSelect(this.supplyTypesSelect, {
                searchEnabled: false,
                itemSelectText: gettext('Wybierz'),
                removeItemButton: true,
            });
            this.supplyTypesChoicesInstance = this.supplyTypesSelect._choices;
            this.supplyTypesChoicesInstance.removeActiveItems();
        }
    },

    _bindEvents() {
        if (this.form._eventsBound) {
            return;
        }
        this.form._eventsBound = true;

        this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));

        const createBtn = document.querySelector(
            '.window-toggle[data-window="reportWindow"]',
        );
        if (createBtn && !createBtn._clickBound) {
            createBtn._clickBound = true;
            createBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.open();
            });
        }

        const closeButtons = this.form.querySelectorAll('.window-close');
        closeButtons.forEach((btn) => {
            if (!btn._clickBound) {
                btn._clickBound = true;
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.closeAndReset();
                });
            }
        });

        const updateCountDebounced = debounce(
            () => this._updateComponentsCount(),
            500,
        );

        if (this.componentGroupSelect) {
            this.componentGroupSelect.addEventListener('change', () => {
                this._onGroupChange();
                updateCountDebounced();
                this._updateSubmitButtonState();
            });
        }

        if (this.componentTypeSelect) {
            this.componentTypeSelect.addEventListener('change', () => {
                updateCountDebounced();
                this._updateSubmitButtonState();
            });
        }

        [this.enovaIdInput, this.rangeFromInput, this.rangeToInput].forEach(
            (input) => {
                if (input) {
                    input.addEventListener('input', () => {
                        updateCountDebounced();
                    });
                }
            },
        );

        if (this.dateFromInput) {
            this.dateFromInput.addEventListener('change', () => {
                this._updateSubmitButtonState();
            });
        }

        if (this.dateToInput) {
            this.dateToInput.addEventListener('change', () => {
                this._updateSubmitButtonState();
            });
        }
    },

    open() {
        this._showModal();
        this._updateSubmitButtonState();
        this._updateComponentsCount();
    },

    async _onGroupChange() {
        const group = this.componentGroupChoicesInstance?.getValue(true);

        try {
            const params = new URLSearchParams();
            if (group) params.append('component_group', group);

            const response = await fetch(
                `/api/reports/filter-options/?${params}`,
            );
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();

            this._updateTypeOptions(data.types);
        } catch (error) {
            console.error('Error loading filter options:', error);
            addAlert(
                'error',
                gettext('Nie udało się załadować opcji filtrowania.'),
            );
        }
    },

    async _updateComponentsCount() {
        const group = this.componentGroupChoicesInstance?.getValue(true) || '';
        const type = this.componentTypeChoicesInstance?.getValue(true) || '';
        const enovaId = this.enovaIdInput?.value || '';
        const rangeFrom = this.rangeFromInput?.value || '';
        const rangeTo = this.rangeToInput?.value || '';

        try {
            const params = new URLSearchParams();
            if (group) params.append('component_group', group);
            if (type) params.append('component_type', type);
            if (enovaId) params.append('enova_id', enovaId);
            if (rangeFrom) params.append('range_from', rangeFrom);
            if (rangeTo) params.append('range_to', rangeTo);

            const response = await fetch(
                `/api/reports/filter-options/?${params}`,
            );
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();

            if (this.matchingComponentsCount) {
                this.matchingComponentsCount.textContent =
                    data.components_count;
            }
            if (this.matchingComponentsCountContainer) {
                this.matchingComponentsCountContainer.classList.remove(
                    'hidden',
                );
            }
        } catch (error) {
            console.error('Error updating components count:', error);
        }
    },

    _updateTypeOptions(types, typeToPreserve = null) {
        if (!this.componentTypeChoicesInstance) {
            console.warn('Type Choices instance not found during update.');
            return;
        }

        let finalSelectedType = '';
        const currentValue = this.componentTypeChoicesInstance.getValue(true);

        if (typeToPreserve && types.includes(typeToPreserve)) {
            finalSelectedType = typeToPreserve;
        } else if (currentValue && types.includes(currentValue)) {
            finalSelectedType = currentValue;
        }

        this.componentTypeChoicesInstance.destroy();
        this.componentTypeChoicesInstance = null;
        this.componentTypeSelect._choices = null;
        this.componentTypeSelect.innerHTML = '';

        const allTypesOption = new Option(
            gettext('Wszystkie typy'),
            '',
            false,
            false,
        );
        this.componentTypeSelect.add(allTypesOption);

        types.forEach((type) => {
            const option = new Option(type, type, false, false);
            this.componentTypeSelect.add(option);
        });

        this.componentTypeSelect.value = finalSelectedType;

        initializeSelect(this.componentTypeSelect, {
            searchEnabled: false,
            itemSelectText: gettext('Wybierz'),
        });

        this.componentTypeChoicesInstance = this.componentTypeSelect._choices;
    },

    async handleFormSubmit(event) {
        event.preventDefault();

        if (this.isSubmitting) {
            console.warn('Form is already being submitted, ignoring...');
            return;
        }

        const payload = this._buildPayload();

        try {
            const validationResponse = await sendPostRequest(
                '/api/reports/validate/',
                payload,
            ).then((res) => res.json());

            if (!validationResponse.valid) {
                addAlert('error', validationResponse.message);
                return;
            }
        } catch (error) {
            console.error('Validation error:', error);
            addAlert(
                'error',
                error.message ||
                    gettext('Nie udało się zwalidować parametrów raportu.'),
            );
            return;
        }

        this.isSubmitting = true;
        this._setLoadingState(true);

        try {
            const response = await sendPostRequest(
                '/api/reports/create/',
                payload,
            ).then((res) => res.json());

            addAlert(
                'success',
                gettext('Pomyślnie zlecono wygenerowanie nowego raportu.'),
            );

            document.dispatchEvent(
                new CustomEvent('reportCreated', {
                    detail: {
                        reportId: response.id,
                        reportData: response,
                    },
                }),
            );

            this.closeAndReset();
        } catch (error) {
            console.error('Error creating report:', error);
            addAlert(
                'error',
                error.message || gettext('Wystąpił nieoczekiwany błąd.'),
            );
        } finally {
            this.isSubmitting = false;
            this._setLoadingState(false);
        }
    },

    resetModal() {
        if (this.componentGroupChoicesInstance) {
            this.componentGroupChoicesInstance.setChoiceByValue('');
        }

        if (this.componentTypeChoicesInstance) {
            this.componentTypeChoicesInstance.setChoiceByValue('');
        }

        if (this.stationsChoicesInstance) {
            this.stationsChoicesInstance.removeActiveItems();
        }

        if (this.supplyTypesChoicesInstance) {
            this.supplyTypesChoicesInstance.removeActiveItems();
        }

        this.form.reset();

        if (this.includeEmptySupplyTypesCheckbox) {
            this.includeEmptySupplyTypesCheckbox.checked = false;
        }

        if (this.matchingComponentsCount) {
            this.matchingComponentsCount.textContent = '0';
        }
        if (this.matchingComponentsCountContainer) {
            this.matchingComponentsCountContainer.classList.add('hidden');
        }

        this._refreshAllOptions();
        this._updateSubmitButtonState();
    },

    async _refreshAllOptions() {
        try {
            const response = await fetch('/api/reports/filter-options/');
            if (response.ok) {
                const data = await response.json();
                this._updateTypeOptions(data.types);
                this._updateComponentsCount();
            }
        } catch (error) {
            console.error('Error refreshing options:', error);
        }
    },

    closeAndReset() {
        this.modalContainer.classList.remove('window-modal-open');
        this.form.classList.remove('window-open');
        saveWindowState(this.form);
        this.resetModal();
        this._setLoadingState(false);
        this.isSubmitting = false;
    },

    _updateSubmitButtonState() {
        const hasDateFrom = this.dateFromInput?.value;
        const hasDateTo = this.dateToInput?.value;

        const isValid = hasDateFrom && hasDateTo && !this.isSubmitting;

        this.submitBtn.disabled = !isValid;
        this.submitBtn.classList.toggle('disabled', !isValid);
    },

    _buildPayload() {
        const componentGroup =
            this.componentGroupChoicesInstance?.getValue(true) || '';
        const componentType =
            this.componentTypeChoicesInstance?.getValue(true) || '';
        const enovaId = this.enovaIdInput?.value || null;
        const rangeFrom = this.rangeFromInput?.value
            ? parseInt(this.rangeFromInput.value, 10)
            : null;
        const rangeTo = this.rangeToInput?.value
            ? parseInt(this.rangeToInput.value, 10)
            : null;

        const measurementStationValues =
            this.stationsChoicesInstance?.getValue(true) || [];

        const stationIds = measurementStationValues
            .map((sid) => parseInt(sid, 10))
            .filter((sid) => !isNaN(sid) && sid > 0);

        const dateFrom = formatDateForAPI(this.dateFromInput);
        const dateTo = formatDateForAPI(this.dateToInput);

        const supplyTypes =
            this.supplyTypesChoicesInstance?.getValue(true) || [];
        const includeEmpty =
            this.includeEmptySupplyTypesCheckbox?.checked || false;

        return {
            component_group: componentGroup,
            component_type: componentType,
            enova_id: enovaId,
            range_from: rangeFrom,
            range_to: rangeTo,
            station_ids: stationIds,
            date_from: dateFrom,
            date_to: dateTo,
            supply_types: supplyTypes,
            include_empty_supply_types: includeEmpty,
        };
    },

    _setLoadingState(isLoading) {
        if (this.btnText) {
            this.btnText.classList.toggle('hidden', isLoading);
        }
        if (this.btnLoader) {
            this.btnLoader.classList.toggle('hidden', !isLoading);
        }
        this.submitBtn.disabled = isLoading;
        if (isLoading) {
            this.submitBtn.classList.add('disabled');
        } else {
            this.submitBtn.classList.remove('disabled');
        }
    },

    _showModal() {
        this.modalContainer.classList.add('window-modal-open');
        this.form.classList.add('window-open');
    },
};

window.reportPopup = reportPopup;

export function initializeReportPopup() {
    window.reportPopup.init();
}
